import {
  Plus,
  ChevronsRight,
  ChevronsDown,
  Copy,
  AlertTriangle,
} from 'lucide-react'
import { cn } from '@/utils/cn'
import { useCltWizardStore } from '@/stores/useCltWizardStore'
import { useMilestones } from '@/hooks/useCltReference'
import { ValueChooserPopover } from './ValueChooserPopover'
import { CopyFromColumnPopover } from './CopyFromColumnPopover'
import {
  RULE_MODE_STYLE,
  cellKey,
  isRuleInvalid,
  sortMilestonesByOrder,
  summarizeRule,
  type Labeled,
  type SubstepKey,
} from './clt.mock'

interface MatrixGridProps {
  substep: SubstepKey
}

/**
 * Step 1 matrix: Milestones (rows) × Stocking Strategies (columns).
 * Each cell opens the Value Chooser. Column headers and row headers expose a
 * "bulk apply" chooser to fill an entire column / row at once.
 */
export function MatrixGrid({ substep }: MatrixGridProps) {
  const matrix = useCltWizardStore(s => s.matrices[substep])
  const strategyGroups = useCltWizardStore(s => s.strategyGroups)
  const setCell = useCltWizardStore(s => s.setCell)
  const applyRuleToRow = useCltWizardStore(s => s.applyRuleToRow)
  const applyRuleToColumn = useCltWizardStore(s => s.applyRuleToColumn)
  const { data: milestoneList = [] } = useMilestones()
  // Backend stores the milestone label in rules' milestone_id / vs_milestone_id
  // (the UUIDs from /milestones aren't used as foreign keys). Use the label as
  // the local id so matrix keys, lookups, and the save payload all line up.
  const milestones: Labeled[] = sortMilestonesByOrder(
    milestoneList.map(m => ({
      id: m.label,
      label: m.label,
    }))
  )

  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-muted/40">
            <th className="sticky left-0 z-10 min-w-[220px] border-b border-r border-border bg-muted/40 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Milestone \ Strategy Group
            </th>
            {strategyGroups.map(strategy => (
              <th
                key={strategy.id}
                className="min-w-[200px] border-b border-r border-border px-3 py-2.5 text-center last:border-r-0"
              >
                <div className="flex flex-col items-center gap-1.5">
                  <span className="text-sm font-semibold text-foreground">
                    {strategy.label}
                  </span>
                  <span className="text-[11px] font-medium text-muted-foreground">
                    {strategy.codes.length > 0
                      ? `Codes ${strategy.codes.join(', ')}`
                      : 'No codes'}
                  </span>
                  <div className="flex items-center gap-1">
                    <ValueChooserPopover
                      value={null}
                      align="center"
                      title={`Apply to entire ${strategy.label} column`}
                      milestones={milestones}
                      onApply={rule =>
                        rule &&
                        applyRuleToColumn(
                          substep,
                          strategy.id,
                          rule,
                          milestones
                        )
                      }
                    >
                      <button
                        type="button"
                        title={`Apply a rule to the whole ${strategy.label} column`}
                        className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-primary cursor-pointer"
                      >
                        <ChevronsDown className="h-3 w-3" />
                        Fill
                      </button>
                    </ValueChooserPopover>
                    <CopyFromColumnPopover
                      targetSubstep={substep}
                      targetStrategyId={strategy.id}
                      targetStrategyLabel={strategy.label}
                      milestones={milestones}
                    >
                      <button
                        type="button"
                        title={`Copy another column into ${strategy.label}`}
                        className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-primary cursor-pointer"
                      >
                        <Copy className="h-3 w-3" />
                        Copy From
                      </button>
                    </CopyFromColumnPopover>
                  </div>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {milestones.map(milestone => (
            <tr key={milestone.id} className="group">
              <th className="sticky left-0 z-10 border-b border-r border-border bg-card px-4 py-3 text-left align-middle">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-foreground">
                    {milestone.label}
                  </span>
                  <ValueChooserPopover
                    value={null}
                    align="start"
                    excludeMilestoneId={milestone.id}
                    title={`Apply to entire ${milestone.label} row`}
                    milestones={milestones}
                    onApply={rule =>
                      rule && applyRuleToRow(substep, milestone.id, rule)
                    }
                  >
                    <button
                      type="button"
                      title={`Apply a rule to the whole ${milestone.label} row`}
                      className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-primary cursor-pointer"
                    >
                      <ChevronsRight className="h-3 w-3" />
                      Fill
                    </button>
                  </ValueChooserPopover>
                </div>
              </th>

              {strategyGroups.map(strategy => {
                const key = cellKey(milestone.id, strategy.id)
                const rule = matrix[key]
                const invalid = rule ? isRuleInvalid(rule, milestone.id) : false
                return (
                  <td
                    key={strategy.id}
                    className="border-b border-r border-border p-1.5 align-middle last:border-r-0"
                  >
                    <ValueChooserPopover
                      value={rule ?? null}
                      allowClear={Boolean(rule)}
                      excludeMilestoneId={milestone.id}
                      title={`${milestone.label} · ${strategy.label}`}
                      milestones={milestones}
                      onApply={next =>
                        setCell(substep, milestone.id, strategy.id, next)
                      }
                    >
                      <button
                        type="button"
                        title={
                          invalid
                            ? 'This rule needs a reference milestone'
                            : undefined
                        }
                        className={cn(
                          'flex min-h-[52px] w-full flex-col items-start justify-center gap-0.5 rounded-md border px-3 py-2 text-left text-xs transition-colors cursor-pointer',
                          invalid
                            ? 'border-destructive bg-destructive/10 text-destructive hover:bg-destructive/15'
                            : rule
                              ? RULE_MODE_STYLE[rule.mode]
                              : 'border-dashed border-border text-muted-foreground hover:border-primary/40 hover:bg-muted/40'
                        )}
                      >
                        {rule ? (
                          <span
                            className={cn(
                              'flex items-center gap-1.5 font-medium',
                              invalid ? 'text-destructive' : 'text-foreground'
                            )}
                          >
                            {invalid && (
                              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                            )}
                            {summarizeRule(rule, milestones)}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <Plus className="h-3 w-3" />
                            Set rule
                          </span>
                        )}
                      </button>
                    </ValueChooserPopover>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
